import React, { useState, useEffect, useRef } from 'react';
import { Trash2 } from 'lucide-react';
import { checkPlacesRateLimit } from '../utils';
import { db, auth } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { appId } from '../constants';

const CitySearch = React.memo(({ index, city, onCityChange, placeholder, className, types, locationBias, dropdownUp = false, dropdownAlign = 'left', minimal = false, preferName = false }) => {
  const [inputValue, setInputValue] = useState(city?.name || '');
  const [predictions, setPredictions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const [focusedPredictionIndex, setFocusedPredictionIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const placesLibraryRef = useRef(null);
  const sessionTokenRef = useRef(null);
  const isSelectingRef = useRef(false);
  const lastRequestIdRef = useRef(0);
  const searchTimeoutRef = useRef(null);

  // Helper to refresh session token
  const refreshSessionToken = () => {
    if (placesLibraryRef.current?.AutocompleteSessionToken) {
      sessionTokenRef.current = new placesLibraryRef.current.AutocompleteSessionToken();
      if (import.meta.env.DEV) console.log('[CitySearch] New session token generated');
    }
  };

  // Initialize Services
  useEffect(() => {
    const init = async () => {
      if (window.google?.maps) {
        try {
          if (!window.google.maps.places) {
            const lib = await window.google.maps.importLibrary('places');
            placesLibraryRef.current = lib;
          } else {
            placesLibraryRef.current = window.google.maps.places;
          }
          refreshSessionToken();
        } catch (e) {
          console.error('[CitySearch] Failed to load Google Places (New):', e);
        }
      }
    };
    init();
  }, []);

  // Sync local value with prop
  useEffect(() => {
    const displayValue = preferName
      ? city?.name || city?.address || ''
      : city?.address || city?.name || '';
    if (
      displayValue !== inputValue &&
      !isSearching &&
      !isSelectingRef.current &&
      !showPredictions
    ) {
      setInputValue(displayValue);
    }
  }, [city?.name, city?.address, isSearching, inputValue, preferName, showPredictions]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setIsSearching(true);
    setShowPredictions(true);
    setFocusedPredictionIndex(-1);

    if (value.trim().length < 3) {
      setPredictions([]);
      setIsSearching(false);
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      const requestId = ++lastRequestIdRef.current;

      let fallbackTriggered = false;
      const triggerFallback = () => {
        if (fallbackTriggered || requestId !== lastRequestIdRef.current) return;
        fallbackTriggered = true;
        if (import.meta.env.DEV) console.log(`[CitySearch] Triggering Open-Meteo fallback for: ${value}`);

        fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(value)}&count=5&language=en&format=json`
        )
          .then((res) => res.json())
          .then((data) => {
            if (requestId !== lastRequestIdRef.current) return;
            if (data.results) {
              setPredictions(
                data.results.map((r) => ({
                  description: `${r.name}, ${r.admin1 || ''} ${r.country_code.toUpperCase()}`
                    .trim()
                    .replace(/  +/g, ' '),
                  place_id: `om-${r.id}`,
                  isFallback: true,
                  lat: r.latitude,
                  lng: r.longitude,
                  timezone: r.timezone,
                }))
              );
            } else {
              setPredictions([]);
            }
            setIsSearching(false);
          })
          .catch((err) => {
            console.error('[CitySearch] Fallback failed:', err);
            if (requestId === lastRequestIdRef.current) setIsSearching(false);
          });
      };

      if (placesLibraryRef.current?.AutocompleteSuggestion) {
        if (!types) {
          // Pure city search. Google Places is expensive and unnecessary. Use Open-Meteo!
          triggerFallback();
          return;
        }

        const googleTimeout = setTimeout(() => {
          if (requestId === lastRequestIdRef.current && isSearching) {
            console.warn('[CitySearch] Google Places (New) timeout, falling back...');
            triggerFallback();
          }
        }, 1500);

        try {
          if (!sessionTokenRef.current) refreshSessionToken();

          const request = { 
            input: value, 
            language: 'en-US',
            sessionToken: sessionTokenRef.current 
          };
          
          if (types) {
            // For address mode, remove the types constraint entirely to allow POIs, establishments, etc.
            if (!types.includes('geocode')) {
              request.includedPrimaryTypes = types;
            }
          }

          if (locationBias && locationBias.lat && locationBias.lng) {
            request.locationBias = {
              center: { lat: parseFloat(locationBias.lat), lng: parseFloat(locationBias.lng) },
              radius: 50000, // 50km
            };
          }

          if (!checkPlacesRateLimit()) {
            console.warn('[CitySearch] Rate limit hit. Falling back.');
            triggerFallback();
            return;
          }

          placesLibraryRef.current.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
            .then(({ suggestions }) => {
              if (requestId !== lastRequestIdRef.current) return;
              clearTimeout(googleTimeout);

              if (suggestions && suggestions.length > 0) {
                if (import.meta.env.DEV) console.log('[CitySearch] Google Places (New) returned results');
                setPredictions(suggestions.map(s => ({
                  description: s.placePrediction.text.toString(),
                  placePrediction: s.placePrediction,
                  place_id: s.placePrediction.placeId,
                  isFallback: false
                })));
                setIsSearching(false);
              } else {
                console.warn(`[CitySearch] Google Places (New) returned no results, falling back...`);
                triggerFallback();
              }
            })
            .catch(e => {
              console.error('[CitySearch] Google Places (New) fetch error:', e);
              triggerFallback();
            });
        } catch (e) {
          console.error('[CitySearch] Google Places (New) call crashed:', e);
          clearTimeout(googleTimeout);
          triggerFallback();
        }
      } else {
        triggerFallback();
      }
    }, 1000);
  };

  const handleSelect = (pred) => {
    isSelectingRef.current = true;
    setShowPredictions(false);
    setFocusedPredictionIndex(-1);

    if (pred.isFallback) {
      const finalName = pred.description;
      setInputValue(finalName);
      onCityChange(index, {
        name: finalName,
        lat: pred.lat,
        lng: pred.lng,
        timezone: pred.timezone,
      });
      refreshSessionToken();
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 500);
      return;
    }

    if (placesLibraryRef.current && pred.placePrediction) {
      const place = pred.placePrediction.toPlace();
      
      if (!checkPlacesRateLimit()) {
        console.warn('[CitySearch] Rate limit hit for fetchFields.');
        return;
      }

      // Use permissive fields. 'formattedAddress' for addresses, 'displayName' for cities.
      place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'],
        sessionToken: sessionTokenRef.current
      }).then(async () => {
        let isVenue = types && place.displayName && place.displayName !== place.formattedAddress;
        
        // Extract city name from address components
        const localityComponent = place.addressComponents?.find(c => c.types.includes('locality') || c.types.includes('postal_town'));
        const extractedCity = localityComponent ? localityComponent.longText : place.displayName;
        
        // Check for alias
        let finalName = place.displayName;
        let venueName = isVenue ? place.displayName : null;
        let aliasText = null;

        if (auth.currentUser && place.id) {
          try {
            const aliasRef = doc(db, 'artifacts', appId, 'public', 'data', 'aliases', `${auth.currentUser.uid}_${place.id}`);
            const aliasSnap = await getDoc(aliasRef);
            if (aliasSnap.exists() && aliasSnap.data().alias) {
              aliasText = aliasSnap.data().alias;
              venueName = `Alias: ${aliasText}`;
              finalName = `Alias: ${aliasText}`;
              isVenue = true;
            }
          } catch (e) {
            console.error('Error fetching alias:', e);
          }
        }
        
        const address = types ? place.formattedAddress : null;
        
        const loc = place.location;

        setInputValue(finalName || place.displayName || '');

        fetch(`https://api.open-meteo.com/v1/forecast?latitude=${loc.lat()}&longitude=${loc.lng()}&current=temperature_2m&timezone=auto`)
          .then((res) => res.json())
          .then((data) => {
            const tz = data.timezone;
            if (types) {
              onCityChange(index, {
                name: extractedCity || address,
                placeName: place.displayName || extractedCity || address,
                placeId: place.id || pred.place_id,
                city: localityComponent?.longText || null,
                venueName: venueName,
                address: address, // Keep full address if needed
                lat: loc.lat(),
                lng: loc.lng(),
                timezone: tz,
              });
            } else {
              onCityChange(index, {
                name: place.displayName,
                placeName: place.displayName,
                placeId: place.id || pred.place_id,
                lat: loc.lat(),
                lng: loc.lng(),
                timezone: tz,
              });
            }
          })
          .catch(() => {
            if (types) {
              onCityChange(index, {
                name: extractedCity || address,
                placeName: place.displayName || extractedCity || address,
                placeId: place.id || pred.place_id,
                city: localityComponent?.longText || null,
                venueName: venueName,
                address: address,
                lat: loc.lat(),
                lng: loc.lng(),
              });
            } else {
              onCityChange(index, {
                name: place.displayName,
                placeName: place.displayName,
                placeId: place.id || pred.place_id,
                lat: loc.lat(),
                lng: loc.lng(),
              });
            }
          });
        refreshSessionToken();
        setTimeout(() => {
          isSelectingRef.current = false;
        }, 500);
      }).catch(e => {
        console.error('[CitySearch] Error fetching place fields:', e);
        refreshSessionToken();
        isSelectingRef.current = false;
      });
    } else {
      refreshSessionToken();
      setTimeout(() => {
        isSelectingRef.current = false;
      }, 500);
    }
  };

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowPredictions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div className={`flex flex-col gap-1 w-full relative ${className}`} ref={dropdownRef}>
      <div className={`flex items-center gap-2 bg-zinc-50 border-2 border-zinc-200 rounded ${minimal ? 'px-2 py-1' : 'px-3 py-2'} focus-within:border-zinc-400 transition-colors relative`}>
        <input
          type="text"
          data-testid={`city-input-real-${index}`}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (showPredictions && predictions.length > 0) {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setFocusedPredictionIndex(prev => prev < predictions.length - 1 ? prev + 1 : 0);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setFocusedPredictionIndex(prev => prev > 0 ? prev - 1 : predictions.length - 1);
              } else if (e.key === 'Enter') {
                e.preventDefault();
                if (focusedPredictionIndex >= 0 && focusedPredictionIndex < predictions.length) {
                  handleSelect(predictions[focusedPredictionIndex]);
                } else {
                  setShowPredictions(false);
                  onCityChange(index, { ...city, name: inputValue });
                  e.target.blur();
                }
              } else if (e.key === 'Escape') {
                setShowPredictions(false);
                setFocusedPredictionIndex(-1);
              }
            }
          }}
          onFocus={() => setShowPredictions(true)}
          onBlur={() => {
            if (isSelectingRef.current) return;
            setTimeout(() => {
              if (!isSelectingRef.current) {
                setShowPredictions(false);
                onCityChange(index, { ...city, name: inputValue });
              }
            }, 200);
          }}
          placeholder={
            placeholder || (index === 0 ? 'Home Address...' : `Location ${index + 1}...`)
          }
          className={`flex-1 min-w-0 bg-transparent border-none outline-none font-bold placeholder:text-zinc-300 truncate ${minimal ? 'text-xs' : 'text-sm'}`}
        />
        {city?.name && (
          <button
            type="button"
            onClick={() => {
              setInputValue('');
              onCityChange(index, { name: '', lat: null, lng: null, timezone: null });
            }}
            className="p-1 text-zinc-300 hover:text-red-500 transition-colors"
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
      {showPredictions && predictions.length > 0 && (
        <div
          data-testid="city-predictions-list"
          className={`absolute ${dropdownUp ? 'bottom-full mb-1' : 'top-full mt-1'} ${dropdownAlign === 'right' ? 'right-0' : 'left-0'} w-max min-w-full max-w-[90vw] z-[100] bg-white border border-zinc-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100`}
        >
          {predictions.map((pred, i) => {
            const isFocused = i === focusedPredictionIndex;
            return (
              <button
                key={pred.place_id}
                data-testid={`city-prediction-item-${i}`}
                onClick={() => handleSelect(pred)}
                className={`w-full text-left ${minimal ? 'px-3 py-2 text-xs' : 'px-4 py-3 text-sm'} transition-colors border-b border-zinc-50 last:border-none flex items-start gap-3 group ${isFocused ? 'bg-zinc-100' : 'hover:bg-zinc-50'}`}
              >
                <span className={`font-medium whitespace-normal break-words ${isFocused ? 'text-blue-700' : 'text-zinc-700 group-hover:text-zinc-900'}`}>
                  {pred.description}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});

CitySearch.displayName = 'CitySearch';

export default CitySearch;
